import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Collapse,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { apiServices } from "../../../services/apiServices";

const fmt = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(parseFloat(v));

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

const getCustomerName = (email) => {
  if (!email || email.includes("anonymous")) return "Guest";
  const local = email.split("@")[0];
  return local
    .split(/[._-]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const avatarBg = (email) => {
  const palette = [
    "#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6",
  ];
  let h = 0;
  for (const c of email || "?") h = c.charCodeAt(0) + ((h << 5) - h);
  return palette[Math.abs(h) % palette.length];
};

const initials = (email) => {
  if (!email || email.includes("anonymous")) return "G";
  const parts = email.split("@")[0].split(/[._-]/);
  return parts.length > 1
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};

export default function ShopifyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    apiServices
      .getAPI("shopify/orders")
      .then((res) => {
        const data = res?.data;
        setOrders(Array.isArray(data) ? data : data?.data || []);
      })
      .catch((e) => setError(e?.message || "Failed to fetch orders"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => setPage(0), [search]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return orders;
    return orders.filter(
      (o) =>
        o.name?.toLowerCase().includes(q) ||
        getCustomerName(o.email).toLowerCase().includes(q) ||
        o.email?.toLowerCase().includes(q) ||
        o.line_items?.some((i) => i.title?.toLowerCase().includes(q))
    );
  }, [orders, search]);

  const paginated = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const totalRevenue = orders.reduce((s, o) => s + parseFloat(o.total_price || 0), 0);

  return (
    <Box sx={{ width: "100%", overflow: "hidden", px: 3, py: 2, pr: 4, mr: 4 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, mr: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ color: "#214F7D" }}>
            Shopify Orders
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            I Am Peacekeeper Store · Order Management
          </Typography>
        </Box>
      </Box>

      {/* Search Card */}
      {!loading && !error && (
        <Card sx={{ mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.1)", mr: 2 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={8}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search order, customer, product…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#999" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ backgroundColor: "#fff" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="text.secondary">
                  {filtered.length} orders
                  {orders.length > 0 && (
                    <> · Revenue {fmt(totalRevenue)}</>
                  )}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Paper sx={{ width: "100%", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", overflow: "auto", pr: 2, mr: 2 }}>
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                <TableCell sx={{ fontWeight: 600, color: "#214F7D", py: 1, fontSize: "0.875rem", minWidth: 100 }}>
                  Order
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#214F7D", py: 1, fontSize: "0.875rem", minWidth: 180 }}>
                  Customer
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#214F7D", py: 1, fontSize: "0.875rem", minWidth: 220 }}>
                  Products
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#214F7D", py: 1, fontSize: "0.875rem", minWidth: 110 }}>
                  Date
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#214F7D", py: 1, fontSize: "0.875rem", minWidth: 90, textAlign: "right" }}>
                  Price
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#214F7D", py: 1, fontSize: "0.875rem", width: 56 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress sx={{ color: "#214F7D" }} />
                    <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                      Loading orders…
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {error && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="error">⚠️ {error}</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && !error && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No orders match your search.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && !error &&
                paginated.map((order) => {
                  const isOpen = expanded === order.name;
                  const name = getCustomerName(order.email);
                  const bg = avatarBg(order.email || order.name);
                  const ini = initials(order.email || "");
                  const first = order.line_items?.[0];
                  const extra = (order.line_items?.length || 1) - 1;
                  return (
                    <React.Fragment key={order.name}>
                      <TableRow
                        hover
                        sx={{
                          "&:hover": { backgroundColor: "#f5f5f5" },
                          ...(isOpen && { backgroundColor: "#f5f3ff" }),
                        }}
                        onClick={() => setExpanded(isOpen ? null : order.name)}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block",
                              bgcolor: "#f0f0ee",
                              borderRadius: 1,
                              px: 1,
                              py: 0.25,
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              color: "#555",
                            }}
                          >
                            {order.name}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {name}
                              </Typography>
                              {order.email && !order.email.includes("anonymous") && (
                                <Typography variant="caption" color="text.secondary">
                                  {order.email}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {first && (
                            <Typography variant="body2">
                              <Box
                                component="span"
                                sx={{
                                  bgcolor: "#f0f0ee",
                                  color: "#777",
                                  borderRadius: 0.5,
                                  px: 0.5,
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  mr: 0.5,
                                }}
                              >
                                ×{first.quantity}
                              </Box>
                              {first.title}
                              {first.variant_title && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  · {first.variant_title}
                                </Typography>
                              )}
                            </Typography>
                          )}
                          {extra > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              +{extra} more item{extra > 1 ? "s" : ""}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {order.created_at ? fmtDate(order.created_at) : "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.created_at ? fmtTime(order.created_at) : ""}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700}>
                            {fmt(order.total_price)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" sx={{ p: 0 }}>
                            {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, borderBottom: "none", bgcolor: "#f9f7ff" }}>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, px: 2 }}>
                              <Grid container spacing={1.5}>
                                {order.line_items?.map((item) => (
                                  <Grid item xs={12} sm={6} md={4} key={item.id}>
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        borderColor: "#e8e5df",
                                      }}
                                    >
                                      <Typography variant="body2" fontWeight={500}>
                                        {item.name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                                        <span>{fmt(item.price)}</span>
                                        <span>Qty {item.quantity}</span>
                                        {item.tax_lines?.[0] && (
                                          <span>Tax {fmt(item.tax_lines[0].price)}</span>
                                        )}
                                      </Typography>
                                    </Paper>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && !error && filtered.length > 0 && (
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        )}
      </Paper>
    </Box>
  );
}
